import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { AlertModal } from '../../../components/modal/alert.modal';
import { ConfirmModal } from '../../../components/modal/confirm.modal';
import { fetchAdminCorporationRegist } from '../../../api/admin.corporation';

interface CreateCorporationDtoTx {
  name: string;
  address: string;
  sido: string;
  sigungu: string;
  detail: string;
}

const id = 'daum-postcode'; // script가 이미 rending 되어 있는지 확인하기 위한 ID
const src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

const AdminCorporationRegist = () => {
  const postcodeRef = useRef<HTMLDivElement | null>(null); // 주소
  const [formData, setFormData] = useState<CreateCorporationDtoTx | null>(null);
  const { register, handleSubmit, setValue } =
    useForm<CreateCorporationDtoTx>();

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/admin/corporation/list',
      });
    }
  };

  const handleConfirmClose = () => {
    setConfirmModal(false);
  };

  //답변 등록 핸들러
  const handleAnswerRegist = async (data: CreateCorporationDtoTx) => {
    try {
      const response = await fetchAdminCorporationRegist(data);
      if (response.statusCode === 200) {
        setModalMessage('회사 주소가 성공적으로 등록되었습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('회사 주소 등록에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error(error);
      setModalMessage('회사 주소 등록 중 오류가 발생했습니다.');
      setIsModalOpen(true);
    }
  };

  // 폼 제출 시 실행될 함수
  const onSubmit = (data: CreateCorporationDtoTx) => {
    if (data.name.length === 0) {
      setModalMessage('회사명은 필수입니다.');
      setIsModalOpen(true);
      return;
    }
    if (data.address.length === 0) {
      setModalMessage('주소는 필수입니다.');
      setIsModalOpen(true);
      return;
    }

    // 답변 데이터를 formData 상태에 저장하고 컨펌 모달을 띄움
    const submitData: CreateCorporationDtoTx = {
      name: data.name,
      address: data.address,
      sido: data.sido,
      sigungu: data.sigungu,
      detail: data.detail,
    };
    setFormData(submitData);
    setConfirmModal(true);
  };

  const cancleHandle = () => {
    history.push('/admin/notice/list');
  };

  const handleConfirmAction = () => {
    setConfirmModal(false);
    if (formData) {
      handleAnswerRegist(formData);
    }
  };

  // 주소 api 호출
  const loadLayout = () => {
    window.daum.postcode.load(() => {
      const postcode = new window.daum.Postcode({
        oncomplete: function (data) {
          setValue('address', data.address);
          setValue('sido', data.sido);
          setValue('sigungu', data.sigungu);
        },
      });
      postcode.open();
    });
  };

  useEffect(() => {
    const isAlready = document.getElementById(id);

    if (!isAlready) {
      const script = document.createElement('script');
      script.src = src;
      script.id = id;
      document.body.append(script);
    }
  }, []);

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="inner">
          <div className="title">회사 등록</div>
          <div className="detail-info-wrap">
            <ul>
              <li>
                <span>회사명</span>
                <div className="input-box">
                  <input
                    type="text"
                    {...register('name')}
                    placeholder="회사명을 입력하세요."
                  />
                </div>
              </li>
              <li>
                <span>주소</span>
                <div className="input-box">
                  <div className="input-add-btn">
                    <input
                      className="address-input"
                      type="text"
                      {...register('address')}
                      placeholder="주소를 입력하세요."
                      readOnly
                    />
                    <button type="button" onClick={loadLayout}>
                      주소 찾기
                    </button>
                  </div>
                  <input
                    type="text"
                    {...register('detail')}
                    placeholder="상세 주소를 입력하세요."
                  />
                  <div ref={postcodeRef}></div>
                  <input type="hidden" {...register('sido')} />
                  <input type="hidden" {...register('sigungu')} />
                </div>
              </li>
            </ul>
          </div>
          <div className="btn-box">
            <button className="cancle" onClick={cancleHandle}>
              취소
            </button>
            <button className="regist" type="submit">
              등록
            </button>
          </div>
        </div>
      </form>

      {confirmModal && (
        <ConfirmModal
          handleConfirmClose={handleConfirmClose}
          confirmText={'등록하시겠습니까?'}
          onClick={handleConfirmAction} // 확인 시 handleConfirmAction 호출
        />
      )}

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default AdminCorporationRegist;

// style
const Container = styled.div`
  margin-top: 7rem;
  overflow-y: scroll;
  .inner {
    max-width: 70rem;
    width: 100%;
    margin: 0 auto;
    .title {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 2.4rem;
    }
    .detail-info-wrap {
      ul {
        border-top: 2px solid #000;
        border-bottom: 2px solid #000;
        li {
          display: flex;
          align-items: center;
          padding: 0 0.8rem;
          span {
            display: inline-flex;
            width: 9.6rem;
            font-size: 1.4rem;
            font-weight: 600;
            color: #222;
          }
          p {
            display: inline-flex;
            font-size: 1.4rem;
            color: #555;
            &.no-info {
              color: #000;
            }
          }
          .input-box {
            width: 100%;
            padding: 0.6rem 0;
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
            .input-add-btn {
              display: flex;
              gap: 0.8rem;
              button {
                border: none;
                padding: 0 2rem;
                flex: 1 0 auto;
                border-radius: 0 0.8rem 0.8rem 0;
              }
            }
            input[type='text'] {
              display: flex;
              width: 100%;
              min-height: 4.2rem;
              padding: 1.4rem 1.8rem;
              border: 1px solid #d6dbe1;
              border-radius: 0.8rem;
              resize: vertical;
              font-size: 1.4rem;
              font-weight: 400;
              resize: none;
              &::placeholder {
                color: ${(props) => props.theme.searchPlaceholderColor};
              }
              &.address-input {
                &:read-only {
                  background-color: ${(props) => props.theme.gray300};
                  color: ${(props) => props.theme.gray900};
                }
              }
            }
          }
          &:not(:last-child) {
            border-bottom: 1px solid #eee;
          }
          &.content {
            height: 43.9rem;
            align-items: flex-start;
            padding: 1rem;

            .box {
              width: 100%;
              textarea {
                height: 41.3rem;
              }
            }
          }
        }
      }
    }
    .btn-box {
      margin-top: 2.4rem;
      display: flex;
      justify-content: center;
      margin-bottom: 4rem;
      gap: 0.8rem;
      button {
        padding: 1.1rem 1rem;
        white-space: nowrap;
        border: none;
        outline: none;
        border-radius: 0.8rem;
        font-size: 1.5rem;
        width: 10rem;
        &.cancle {
        }
        &.regist {
          background-color: ${(props) => props.theme.btnBlueColor};
          color: ${(props) => props.theme.textWhiteColor};
        }
      }
    }
  }
`;
